import axios from 'axios';

// Dynamically determine API base URL based on current domain
const getApiBaseUrl = () => {
    const currentDomain = window.location.hostname;
    
    console.log('Current domain:', currentDomain); // Debug log
    
    if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
        // Development environment
        const url = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
        console.log('Using development API URL:', url);
        return url;
    } else if (currentDomain === 'admin.jaddid.ae') {
        // Production environment
        const url = process.env.REACT_APP_PROD_API_URL || 'https://api.jaddid.ae/api';
        console.log('Using production API URL:', url);
        return url;
    } else {
        // Fallback - you can customize this based on your needs
        const url = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
        console.log('Using fallback API URL:', url);
        return url;
    }
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: false,
    timeout: 30000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('API Request:', config.method?.toUpperCase(), config.url, config.baseURL); // Debug log
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => {
        console.log('API Response:', response.status, response.config.url); // Debug log
        return response;
    },
    (error) => {
        console.error('API Error:', {
            status: error.response?.status,
            url: error.config?.url,
            message: error.message,
            data: error.response?.data
        });
        
        if (error.response?.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const adminLogin = async (credentials) => {
    try {
        const response = await api.post('/admin/login', credentials);
        return response.data;
    } catch (error) {
        console.error('Login error:', error);
        throw error;
    }
};

// Dashboard APIs
export const getDashboardStats = async () => {
    try {
        const response = await api.get('/admin/dashboard/stats');
        return response.data;
    } catch (error) {
        console.error('Dashboard stats error:', error);
        // Return mock data for development if API fails
        return {
            total_users: 0,
            total_vendors: 0,
            total_vouchers: 0,
            total_revenue: '0.00',
            user_growth: [],
            monthly_revenue: [],
            user_types: []
        };
    }
};

export const getSystemHealth = async () => {
    try {
        const response = await api.get('/admin/system/health');
        return response.data;
    } catch (error) {
        console.error('System health error:', error);
        // Return mock data for development if API fails
        return {
            database_status: 'unknown',
            api_status: 'unknown',
            storage_status: 'unknown'
        };
    }
};

// User Management APIs
export const getUsers = async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
};

export const getUserDetails = async (id) => {
    try {
        const response = await api.get(`/admin/users/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching user details:', error);
        throw error;
    }
};

export const updateUserStatus = async (id, status) => {
    try {
        const response = await api.patch(`/admin/users/${id}/status`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating user status:', error);
        throw error;
    }
};

export const deleteUser = async (id) => {
    try {
        const response = await api.delete(`/admin/users/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

// Vendor Management APIs
export const getVendors = async (params = {}) => {
    const response = await api.get('/admin/vendors', { params });
    return response.data;
};

export const getVendorDetails = async (id) => {
    try {
        const response = await api.get(`/admin/vendors/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching vendor details:', error);
        // Return mock data
        return {
            id: id,
            name: 'Sample Vendor',
            contact_name: 'Contact Person',
            email: 'vendor@example.com',
            phone: '+971501234567',
            address: '123 Business Bay, Dubai',
            city: 'Dubai',
            emirate: 'Dubai',
            status: 'active',
            buying_items: ['Electronics', 'Phones'],
            description: 'Sample vendor description',
            created_at: '2024-01-01T00:00:00Z'
        };
    }
};

export const updateVendorStatus = async (id, status) => {
    const response = await api.patch(`/admin/vendors/${id}/status`, { status });
    return response.data;
};

export const approveVendor = async (id) => {
    const response = await api.patch(`/admin/vendors/${id}/approve`);
    return response.data;
};

// Voucher Supplier Management APIs
export const getVoucherSuppliers = async (params = {}) => {
    try {
        const response = await api.get('/admin/voucher-suppliers', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching voucher suppliers:', error);
        // Return mock data for development
        return {
            suppliers: [
                {
                    id: 1,
                    name: 'Sample Supplier',
                    contact_name: 'John Doe',
                    email: 'supplier@example.com',
                    phone: '+971501234567',
                    status: 'active',
                    voucher_count: 5,
                    created_at: '2024-01-01T00:00:00Z'
                }
            ]
        };
    }
};

export const getVoucherSupplierDetails = async (id) => {
    try {
        const response = await api.get(`/admin/voucher-suppliers/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching voucher supplier details:', error);
        // Return mock data
        return {
            id: id,
            name: 'Sample Supplier',
            contact_name: 'John Doe',
            email: 'supplier@example.com',
            phone: '+971501234567',
            status: 'active',
            voucher_count: 5,
            active_vouchers: 3,
            description: 'Sample supplier description',
            created_at: '2024-01-01T00:00:00Z'
        };
    }
};

export const updateVoucherSupplierStatus = async (id, status) => {
    try {
        const response = await api.patch(`/admin/voucher-suppliers/${id}/status`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating voucher supplier status:', error);
        return { success: true };
    }
};

// Voucher Management APIs
export const getAllVouchers = async (params = {}) => {
    try {
        const response = await api.get('/admin/vouchers', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching vouchers:', error);
        // Return mock data for development
        return {
            vouchers: [
                {
                    id: 1,
                    title: 'Sample Voucher',
                    code: 'SAMPLE2024',
                    supplier_name: 'Sample Supplier',
                    discount_value: 20,
                    status: 'active',
                    valid_until: '2024-12-31T00:00:00Z',
                    redemptions: 10,
                    description: 'Sample voucher description',
                    category: 'General'
                }
            ]
        };
    }
};

export const updateVoucherStatus = async (id, status) => {
    try {
        const response = await api.patch(`/admin/vouchers/${id}/status`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating voucher status:', error);
        return { success: true };
    }
};

// Transaction Management APIs
export const getTransactions = async (params = {}) => {
    const response = await api.get('/admin/transactions', { params });
    return response.data;
};

export const getTransactionDetails = async (id) => {
    try {
        const response = await api.get(`/admin/transactions/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching transaction details:', error);
        throw error;
    }
};

// Analytics APIs
export const getUserAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/users', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching user analytics:', error);
        // Return mock data for development
        return {
            growth: [
                { date: '2024-01-01', users: 100 },
                { date: '2024-01-02', users: 110 },
                { date: '2024-01-03', users: 125 },
                { date: '2024-01-04', users: 140 },
                { date: '2024-01-05', users: 155 }
            ]
        };
    }
};

export const getVendorAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/vendors', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching vendor analytics:', error);
        // Return mock data for development
        return {
            activity: [
                { name: 'Active', value: 65 },
                { name: 'Pending', value: 25 },
                { name: 'Suspended', value: 10 }
            ]
        };
    }
};

export const getVoucherAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/vouchers', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching voucher analytics:', error);
        // Return mock data for development
        return {
            redemptions: [
                { date: '2024-01-01', redemptions: 15 },
                { date: '2024-01-02', redemptions: 22 },
                { date: '2024-01-03', redemptions: 18 },
                { date: '2024-01-04', redemptions: 30 },
                { date: '2024-01-05', redemptions: 25 }
            ]
        };
    }
};

export const getRevenueAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/revenue', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching revenue analytics:', error);
        // Return mock data for development
        return {
            trends: [
                { date: '2024-01-01', revenue: 1500 },
                { date: '2024-01-02', revenue: 1800 },
                { date: '2024-01-03', revenue: 2200 },
                { date: '2024-01-04', revenue: 1900 },
                { date: '2024-01-05', revenue: 2500 }
            ]
        };
    }
};

// System Management APIs
export const getSystemLogs = async (params = {}) => {
    const response = await api.get('/admin/system/logs', { params });
    return response.data;
};

export const backupDatabase = async () => {
    const response = await api.post('/admin/system/backup');
    return response.data;
};

export const getSystemSettings = async () => {
    const response = await api.get('/admin/system/settings');
    return response.data;
};

export const updateSystemSettings = async (settings) => {
    const response = await api.put('/admin/system/settings', settings);
    return response.data;
};

export default api;
